// @flow

// import { isEqual } from "lodash";
import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import Config from "../constants/Config";

import formatCurrency from "../utils/formatCurrency";
import RenderJsxHtml from "../utils/RenderJsxHtml";

import { addToCart } from "../actions/cart";

import usePageFromStore from "../hooks/usePageFromStore";
import useInvertedNav from "../hooks/useInvertedNav";
import useAsyncState from "../hooks/useAsyncState";
import Store from "../store";
import { PrimaryNavPad } from "../components/organisms/PrimaryNav";
import Breadcrumbs from "../components/molecules/Breadcrumbs";
import ShopFooter from "../components/organisms/ShopFooter";
import RelatedContent from "../components/organisms/RelatedContent";
import QuantityControl from "../components/atoms/QuantityControl";
import Button from "../components/atoms/Button";
import AttributeRadio from "../components/atoms/AttributeRadio";
import CategorySection from "../components/molecules/CategorySection";
import Accordions from "../components/organisms/Accordions";
import ProductGallery from "../components/organisms/ProductGallery";
import ServerErrorMessage from "../components/atoms/ServerErrorMessage";
import SizeChartModal from "../components/organisms/SizeChartModal";
import RichTextContent from "../components/molecules/RichTextContent";
import getProductPriceHtml from "../utils/getProductPriceHtml";
import getVariationProductMinMaxPrice from "../utils/getVariationProductMinMaxPrice";
import { ReactComponent as Ring } from "../../assets/images/shape-ring-thick-fill.svg";
import { ReactComponent as Diamond } from "../../assets/images/shape-diamond.svg";

type PropsType = {
    page: any,
};

const ProductDetail = (props: PropsType): React.Node => {
    const dispatch = useDispatch();
    const { loggedIn } = useSelector((state: any): any => state.user);
    const [state, localActions] = useAsyncState();
    const { page } = usePageFromStore();
    const user = Store.getState().user;
    const userDiscount =
        (100 - (user.nonce?.user_discount_percentage ?? 0)) / 100;
    const userType = user.profile ? user.profile.type : "";
    const [attributes, setAttributes] = React.useState([]);
    const [variation, setVariation] = React.useState({});
    const [variationMinMaxPrice, setVariationMinMaxPrice] = React.useState({
        min: 0,
        max: 0,
    });
    const [productImages, setProductImages] = React.useState([]);
    const [quantity, setQuantity] = React.useState(1);
    const [showSizeChart, setShowSizeChart] = React.useState(false);

    const hasVariations =
        page.product_variations && page.product_variations.length;

    const variationInStock = variation
        ? variation.stock_status === "instock"
        : false;

    const isLowStock =
        variation && variation.stock_quantity && variation.low_stock_amount
            ? variation.stock_quantity <= variation.low_stock_amount
            : page.stock_quantity &&
              page.low_stock_amount &&
              Number(page.stock_quantity) <= Number(page.low_stock_amount);

    const variationMaxStock = variation ? variation.stock_quantity : null;

    const maxItemQuantity = page.acf.maximum_quantity
        ? Number(page.acf.maximum_quantity)
        : variationMaxStock;

    const allVariantsOnSale =
        page.product_variations &&
        page.product_variations.filter(variant => variant.on_sale).length ===
            page.product_variations.length;

    // const isUtsDiscount =
    //     page.uts_discountable &&
    //     page.member_discount &&
    //     Config.discountUserTypes.includes(userType);
    const isUtsDiscount =
        !variation.on_sale &&
        variation.uts_discountable &&
        Config.discountUserTypes.includes(userType);

    // const isSaleDiscount =
    //     Number(variation.regular_price) &&
    //     Number(variation.price) !== Number(variation.regular_price) &&
    //     !page.member_discount;
    const isSaleDiscount =
        (!page.product_variations || !page.product_variations.length) &&
        variation.on_sale
            ? true
            : (variation.isVariant && variation.on_sale) || allVariantsOnSale;

    // accordion sections
    const accordionSections = [];
    if (page.acf.product_details) {
        accordionSections.push({
            title: "Product Details",
            content: page.acf.product_details,
            active: true,
        });
    }
    if (page.acf.specifications) {
        accordionSections.push({
            title: "Specifications",
            content: page.acf.specifications,
        });
    }
    if (page.acf.other_information) {
        accordionSections.push({
            title: "Other Information",
            content: page.acf.other_information,
        });
    }

    // invert primary nav color
    useInvertedNav();

    // get the min and max price from variations
    React.useEffect(() => {
        setAttributes(
            page.attributes.map(attribute => {
                const selectedAttribute = page.default_attributes.find(
                    defaultAttribute => defaultAttribute.name === attribute.name
                );
                return {
                    ...attribute,
                    selected: selectedAttribute ? selectedAttribute.option : "", //  attribute.options[0].toLowerCase(),
                };
            })
        );

        const minMaxPrice = getVariationProductMinMaxPrice(
            page.product_variations
        );
        setVariationMinMaxPrice({
            min: minMaxPrice.minPrice,
            max: minMaxPrice.maxPrice,
        });
    }, [page]);

    // attribute change use effect
    React.useEffect(() => {
        const newVariation = getVariationItem(attributes);
        if (newVariation) {
            setVariation({ ...newVariation, isVariant: true });
        } else {
            // no variation found and product, assumme no product variations and use page data
            setVariation(page);
        }
        // product gallery images
        let productImages = [
            ...page.images.map(image => {
                return { ...image, caption: page.name };
            }),
        ];

        if (hasVariations) {
            page.product_variations.forEach(productVariation => {
                if (productVariation.image && productVariation.image.src) {
                    const imageExists = productImages.find(image => {
                        return image.id === productVariation.image.id;
                    });

                    if (!imageExists) {
                        productImages.push({
                            ...productVariation.image,
                            caption: (
                                <div className="capitlize">
                                    {page.name} -{" "}
                                    {productVariation.image.name
                                        .split("-")
                                        .join(" ")}
                                </div>
                            ),
                            variationId: productVariation.id,
                        });
                    }
                }
            });
        }

        setProductImages(productImages);
    }, [attributes, page]); // eslint-disable-line

    // variation change use effect
    React.useEffect(() => {
        // reset quantity
        setQuantity(1);
    }, [variation]);

    const getVariationItem = attributes => {
        const variation = hasVariations
            ? page.product_variations.find(productVariant => {
                  let isMatch = productVariant.attributes.length ? true : false;

                  productVariant.attributes.forEach(productVariantAttribute => {
                      attributes.forEach(setAttribute => {
                          if (
                              setAttribute.name ===
                                  productVariantAttribute.name &&
                              setAttribute.selected !==
                                  productVariantAttribute.option.toLowerCase()
                          ) {
                              isMatch = false;
                          }
                      });
                  });

                  return isMatch;
              })
            : false;

        return variation || null;
    };

    const getDisabledAttributeOptions = attributeToTest => {
        const disabledOptions = [];

        attributeToTest.options.forEach(option => {
            // prepare attributes with test options
            const testAttributes = [];
            attributes.forEach(attribute => {
                const attributeWithTestSelection = {
                    ...attribute,
                    selected:
                        attribute.name.toLowerCase() ===
                        attributeToTest.name.toLowerCase()
                            ? option.toLowerCase()
                            : attribute.selected,
                };

                // only push attribute to test if it has a selected value
                if (attributeWithTestSelection.selected) {
                    testAttributes.push(attributeWithTestSelection);
                }
            });

            const matchedVariationItem = getVariationItem(testAttributes);

            // check to see if variation can be found with attributes
            if (
                !matchedVariationItem ||
                matchedVariationItem.stock_status !== "instock"
            ) {
                disabledOptions.push(option);
            }
        });

        return disabledOptions;
    };

    const handleAttributeChange = e => {
        setAttributes(
            attributes.map(attribute => {
                const selectedValue =
                    attribute.name === e.target.name
                        ? e.target.value.toLowerCase()
                        : attribute.selected;

                return {
                    ...attribute,
                    selected: selectedValue,
                    error: selectedValue ? null : attribute.error,
                };
            })
        );
    };

    const handleQuantityIncrement = () => {
        setQuantity(quantity + 1);
    };

    const handleQuantityDecrement = () => {
        setQuantity(quantity - 1);
    };

    const handleAddToCart = () => {
        // validate attribute selections and show errors
        let attributesSet = true;
        setAttributes(
            attributes.map(attribute => {
                attributesSet = attribute.selected ? true : false;
                return {
                    ...attribute,
                    error: !attribute.selected
                        ? `Please select ${attribute.name}`
                        : null,
                };
            })
        );

        // if no attribute errors and variation exists with stock, add to cart
        if (attributesSet && variation.id && variationInStock) {
            localActions.request();

            dispatch(
                addToCart({
                    id: variation.id,
                    quantity: quantity,
                    variation: variation.attributes.map(variationAttribute => {
                        return {
                            attribute: variationAttribute.name,
                            value: variationAttribute.option,
                        };
                    }),
                })
            )
                .then(res => {
                    localActions.success();

                    // reset quantity
                    setQuantity(1);
                })
                .catch((error: any) => {
                    console.error(error);
                    localActions.fail(
                        error.message ||
                            "Failed to add item to cart, it may be temporarily unavailable."
                    );
                });
        }
    };

    const renderDetailsAccordion = () => {
        return (
            <div>
                <Accordions accordions={accordionSections} />
            </div>
        );
    };

    const renderBreadcrumbs = () => {
        return (
            <Breadcrumbs
                colorInverted={true}
                breadcrumbs={[{ title: "Shop", url: "/shop" }]}
                customPageTitle={page.name}
            />
        );
    };

    const renderCategories = () => {
        if (page.categories && page.categories.length > 0) {
            return (
                <CategorySection
                    categories={page.categories}
                    title="Product category"
                    type="product"
                />
            );
        }

        return null;
    };

    return (
        <div className="product-detail">
            <PrimaryNavPad />
            <div className="product-detail__inner">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-xs-12 col-sm-6 col-lg-7">
                            {/* Mobile Breadcrumbs */}
                            <div className="hide-for-sm">
                                {renderBreadcrumbs()}
                            </div>

                            <div className="product-detail__images">
                                {productImages && productImages.length ? (
                                    <ProductGallery
                                        images={productImages}
                                        variation={variation}
                                    />
                                ) : null}

                                <Ring className="product-detail__ring" />
                                <Diamond className="product-detail__diamond" />
                            </div>

                            {/* Desktop details */}
                            <div className="show-for-sm">
                                {renderDetailsAccordion()}
                            </div>
                        </div>

                        <div className="col-xs-12 col-sm-6 col-md-5 col-md-offset-1 col-lg-4">
                            {/* Desktop breadcrumbs */}
                            <div className="show-for-sm">
                                {renderBreadcrumbs()}
                            </div>

                            {/* Product attribute selection */}
                            <div>
                                <h1 className="product-detail__heading">
                                    {page.name}
                                </h1>

                                {/* Description */}
                                <RichTextContent
                                    className="product-detail__description"
                                    html={page.short_description}
                                />

                                {/* Discounts label */}
                                {isSaleDiscount ||
                                isUtsDiscount ||
                                page.uts_discountable ? (
                                    <div className="product-detail__discounts">
                                        {loggedIn ? (
                                            // logged in
                                            isSaleDiscount ? (
                                                <span className="product-detail__discounts__sale">
                                                    Sale
                                                </span>
                                            ) : isUtsDiscount ? (
                                                "UTS 10% discount applied"
                                            ) : (
                                                ""
                                            )
                                        ) : // logged out -> check if on sale -> check if discountable
                                        isSaleDiscount ? (
                                            <span className="product-detail__discounts__sale">
                                                Sale
                                            </span>
                                        ) : page.uts_discountable ? (
                                            "10% off for UTS students and staff"
                                        ) : (
                                            ""
                                        )}
                                    </div>
                                ) : null}

                                {/* Price */}
                                <div className="product-detail__price">
                                    {hasVariations && !variation.isVariant ? (
                                        <>
                                            <RenderJsxHtml
                                                className={`${
                                                    allVariantsOnSale
                                                        ? "product-detail__price__sale"
                                                        : ""
                                                }`}
                                            >
                                                {getProductPriceHtml({
                                                    type: variation.type,
                                                    price: variation.price,
                                                    regularPrice:
                                                        variation.regular_price,
                                                    salePrice:
                                                        variation.sale_price,
                                                    variationPricesMin:
                                                        variationMinMaxPrice.min,
                                                    variationPricesMax:
                                                        variationMinMaxPrice.max,
                                                    userDiscountPercentage:
                                                        user.nonce
                                                            ?.user_discount_percentage ??
                                                        0,
                                                    onSale: variation.on_sale,
                                                    productDiscountable:
                                                        variation.uts_discountable,
                                                })}
                                            </RenderJsxHtml>
                                        </>
                                    ) : (
                                        <>
                                            <span
                                                className={`${
                                                    isSaleDiscount
                                                        ? "product-detail__price__sale"
                                                        : ""
                                                }`}
                                            >
                                                {formatCurrency(
                                                    variation.sale_price ||
                                                        (variation.uts_discountable &&
                                                        user.nonce
                                                            ?.user_discount_percentage &&
                                                        variation.uts_discountable
                                                            ? (
                                                                  Number(
                                                                      variation.regular_price
                                                                  ) *
                                                                  userDiscount
                                                              ).toFixed(2)
                                                            : variation.regular_price),
                                                    1
                                                )}
                                            </span>
                                            {variation.sale_price ||
                                            (variation.uts_discountable &&
                                                user.nonce
                                                    ?.user_discount_percentage &&
                                                user.nonce
                                                    ?.user_discount_percentage >
                                                    0) ? (
                                                <span className="product-detail__price__original">
                                                    {formatCurrency(
                                                        variation.regular_price,
                                                        1
                                                    )}
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                    <>
                                        {/* <RenderJsxHtml
                                            className={`${
                                                allVariantsOnSale
                                                    ? "product-detail__price__sale"
                                                    : ""
                                            }`}
                                        >
                                            {getProductPriceHtml({
                                                type: variation.type,
                                                price: variation.price,
                                                regularPrice:
                                                    variation.regular_price,
                                                salePrice: variation.sale_price,
                                                variationPricesMin:
                                                    variationMinMaxPrice.min,
                                                variationPricesMax:
                                                    variationMinMaxPrice.max,
                                                userDiscountPercentage:
                                                    user.nonce
                                                        ?.user_discount_percentage ??
                                                    0,
                                                onSale: variation.on_sale,
                                                productDiscountable:
                                                    variation.uts_discountable,
                                            })}
                                        </RenderJsxHtml> */}
                                    </>
                                </div>

                                {attributes.map((attribute, index) => {
                                    return (
                                        <AttributeRadio
                                            key={index}
                                            {...attribute}
                                            onChange={handleAttributeChange}
                                            // Size Chart modal btn
                                            infoBtn={
                                                attribute.name === "Size" ||
                                                attribute.name ===
                                                    "Clothing Size"
                                                    ? {
                                                          label: "Size Chart",
                                                          onClick: () =>
                                                              setShowSizeChart(
                                                                  true
                                                              ),
                                                      }
                                                    : null
                                            }
                                            disabledOptions={getDisabledAttributeOptions(
                                                attribute
                                            )}
                                        />
                                    );
                                })}

                                <SizeChartModal
                                    active={showSizeChart}
                                    onClose={() => setShowSizeChart(false)}
                                />

                                <QuantityControl
                                    label="Quantity"
                                    quantity={quantity}
                                    min={1}
                                    max={maxItemQuantity}
                                    onIncrement={handleQuantityIncrement}
                                    onDecrement={handleQuantityDecrement}
                                    alert={isLowStock ? "Low stock" : ""}
                                />

                                <Button
                                    styles={["green"]}
                                    onClick={handleAddToCart}
                                    disabled={!variationInStock}
                                    loading={state.loading}
                                    success={!!state.success}
                                    successText="Added to cart"
                                >
                                    {variationInStock
                                        ? "Add to cart"
                                        : "Out of stock"}
                                </Button>

                                <ServerErrorMessage>
                                    {state.error}
                                </ServerErrorMessage>
                            </div>

                            {/* Mobile details */}
                            <div className="hide-for-sm">
                                {renderDetailsAccordion()}
                            </div>

                            {/* Desktop categories */}
                            <div className="show-for-sm">
                                <br />
                                <br />
                                {renderCategories()}
                            </div>
                        </div>
                    </div>
                </div>

                <ShopFooter />

                <div className="product-detail__categories">
                    <div className="container-fluid">
                        <div className="hide-for-sm">{renderCategories()}</div>
                    </div>
                </div>

                {page.categories && page.categories.length > 0 ? (
                    <RelatedContent
                        title="Other products you might be interested in..."
                        useDynamicContent={true}
                        categories={page.categories.map(category => {
                            return category.id;
                        })}
                        postType="product"
                        endpoint="/wp-json/shop/v1/products"
                    />
                ) : null}
            </div>
        </div>
    );
};

ProductDetail.preload = ({
    passed: { store },
    location,
}: any): Promise<any> => {
    return Promise.all([]);
};

export default ProductDetail;
